import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  ListSubheader,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { apiErrorMessage } from "../../utils/apiError";
import LabelPreview from "./barcodeMapper/LabelPreview";
import { parseLabel } from "./barcodeMapper/prnParse";
import PawnNavButtons from "./pawnNavButtons";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddIcon from "@mui/icons-material/AddRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const BORDER_MED = "#CBD5E1";
const SURFACE = "#FFFFFF";
const BG_ALT = "#F1F5F9";
const SUCCESS = "#059669";
const AMBER = "#D97706";
const AMBER_BG = "#FFFBEB";
const DANGER = "#DC2626";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const TOKEN_HINTS = {
  STORENAME: "store.name",
  STORE: "store.name",
  BRANCH: "store.name",
  TAGNO: "loan.voucher_no",
  LEDGER: "loan.voucher_no",
  VOUCHERNO: "loan.voucher_no",
  VOUCHER: "loan.voucher_no",
  LOANNO: "loan.voucher_no",
  ITEMCODE: "loan.voucher_no",
  ICODE: "loan.voucher_no",
  DATE: "loan.date",
  LOANDATE: "loan.date",
  DUEDATE: "loan.due_date",
  NAME: "customer.name",
  CUSTNAME: "customer.name",
  CUSTOMER: "customer.name",
  MOBILE: "customer.mobile",
  PHONE: "customer.mobile",
  AMOUNT: "loan.amount",
  LOANAMT: "loan.amount",
  GROSS: "orn.total_gross",
  NET: "orn.total_net",
  QTY: "orn.total_pcs",
  PCS: "orn.total_pcs",
  PURITY: "orn.first_purity",
  FLOOR: "loan.floor_name",
  SCHEME: "loan.scheme_name",
  TARE: "loan.tare_wt",
};

// ─── Which template the screen opens with ───────────────────────────────────
//
// Landing on an empty form when the store already has templates reads as "nothing
// is set up here". The store's default is the one a payment print would actually
// use, so it is the honest thing to show; with no default set, the one most
// recently worked on is the best guess at what the installer came back for.
const stamp = (t) => Date.parse(t?.updated_at || t?.created_at || "") || 0;

const pickOpeningTemplate = (rows) => {
  const live = (rows || []).filter((t) => t.is_active !== false);
  const pool = live.length ? live : rows || [];
  if (!pool.length) return null;
  return (
    pool.find((t) => t.is_default) ||
    [...pool].sort(
      (a, b) => stamp(b) - stamp(a) || (b.id || 0) - (a.id || 0),
    )[0]
  );
};

const blankRule = (token) => ({
  token,
  source: "",
  literal: "",
  transform: "none",
  maxLen: "",
  padTo: "",
  padChar: "",
  align: "left",
});

// Extensions the upload / drop zone will read as template text. Anything else
// dropped on the left column is refused rather than decoded as ISO-8859-1.
const LOADABLE = /\.(prn|txt|zpl|epl|lbl|dat)$/i;

const cardSx = {
  p: { xs: 1.25, sm: 1.75 },
  borderRadius: 2,
  border: `1px solid ${BORDER}`,
  bgcolor: SURFACE,
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const labelSx = {
  fontSize: 11,
  fontWeight: 700,
  color: INK_MUTED,
  letterSpacing: 0.3,
};

// ─── Compact field (blue focus, red outline when the value is missing) ──────
const CField = ({ error, children, ...rest }) => (
  <TextField
    size="small"
    fullWidth
    error={!!error}
    {...rest}
    sx={{
      "& .MuiInputBase-root": {
        fontSize: 13,
        borderRadius: 2,
        background: error ? "#FEF2F2" : SURFACE,
      },
      "& label": { fontSize: 13, color: error ? DANGER : INK_MUTED },
      "& label.Mui-focused": { color: error ? DANGER : BLUE_DARK },
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: error ? DANGER : BORDER_MED,
          borderWidth: error ? "2px" : "1px",
        },
        "&:hover fieldset": { borderColor: error ? DANGER : BLUE },
        "&.Mui-focused fieldset": {
          borderColor: error ? DANGER : BLUE,
          borderWidth: "2px",
        },
      },
      ...rest.sx,
    }}
  >
    {children}
  </TextField>
);

const SectionHeader = ({ icon, label, right }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1.25}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: 1.5,
        bgcolor: `${BLUE}15`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: BLUE,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Typography
      fontWeight={700}
      fontSize={13}
      color={INK}
      sx={{ flex: 1, minWidth: 0 }}
      noWrap
    >
      {label}
    </Typography>
    {right}
  </Box>
);

const PawnBarcodeMapper = ({ embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { currentStoreInfo, userInfo } = useContext(AutoTaskAuthContext) || {};
  const storeId = currentStoreInfo?.id;
  const userId = userInfo?.id || null;

  const fileRef = useRef(null);
  const previewTimer = useRef(null);
  // The store whose templates have already been auto-opened. The pick happens
  // once on arrival — re-listing after a save or a delete must never yank the
  // form away from whatever the user is editing.
  const autoOpenedFor = useRef(null);

  const [catalog, setCatalog] = useState({
    groups: [],
    transforms: [],
    samples: {},
  });
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    template_name: "",
    description: "",
    printer_model: "",
    share_name: "",
    variant_no: 1,
    copies: 1,
    is_default: false,
  });
  const [prnSource, setPrnSource] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [rules, setRules] = useState([]);
  const [preview, setPreview] = useState(null);

  // Live-preview geometry
  const [labelMm, setLabelMm] = useState({ w: 92, h: 15 });
  const [zoom, setZoom] = useState(1);
  const [showRaw, setShowRaw] = useState(false);
  // Off by default: the preview's job is to show the tag as it will come off the
  // printer, and an unmapped token prints nothing. Switched on it becomes an
  // inspection aid answering "if I map this, where would it land?".
  const [showUnmapped, setShowUnmapped] = useState(false);

  // Physical die cut of the tag stock: a rounded body carrying the print plus a
  // narrow strap that gets wrapped around the ornament. Only `printArea` stays
  // readable once the tag is fitted, so it is the number that decides whether a
  // design works. Measured off the shop's own stock (92 × 15 mm overall).
  const [tag, setTag] = useState({
    shape: "tag",
    printArea: 52,
    tailHeight: 3.5,
  });

  const [errors, setErrors] = useState({});
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  // Confirm / prompt dialogs
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
    busy: false,
  });
  const [voucherAsk, setVoucherAsk] = useState({
    open: false,
    value: "",
    busy: false,
  });

  const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  // ── Load the field catalogue + this store's saved templates ───────────────
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/pawn/label-templates/fields`)
      .then((r) =>
        setCatalog(r.data?.data || { groups: [], transforms: [], samples: {} }),
      )
      .catch(() => notify("Could not load the loan field list", "error"));
  }, []);

  const loadTemplates = useCallback(async () => {
    if (!storeId) return [];
    try {
      const r = await axios.get(
        `${API_BASE_URL}/api/pawn/label-templates/store/${storeId}`,
      );
      const rows = r.data?.data || [];
      setTemplates(rows);
      return rows;
    } catch {
      setTemplates([]);
      return [];
    }
  }, [storeId]);

  // ── Scan: find the tokens in whatever source we have ─────────────────────
  const runScan = useCallback(async (source, { seedHints = true } = {}) => {
    if (!source || !source.trim()) {
      setAnalysis(null);
      setRules([]);
      setPreview(null);
      return;
    }
    setScanning(true);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/pawn/label-templates/scan`,
        {
          prn_source: source,
        },
      );
      const data = r.data?.data || null;
      setAnalysis(data);
      if (r.data?.message) notify(r.data.message, "warning");

      setRules((prev) => {
        const byToken = new Map(prev.map((x) => [x.token, x]));
        return (data?.tokens || []).map((token) => {
          const existing = byToken.get(token);
          if (existing) return existing;
          const rule = blankRule(token);
          if (seedHints && TOKEN_HINTS[token]) rule.source = TOKEN_HINTS[token];
          return rule;
        });
      });
    } catch (e) {
      notify(apiErrorMessage(e) || "Could not parse the template", "error");
    } finally {
      setScanning(false);
    }
  }, []);

  // ── Live preview, debounced (mapping edits fire on every keystroke) ───────
  useEffect(() => {
    if (!prnSource.trim()) {
      setPreview(null);
      return;
    }
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      axios
        .post(`${API_BASE_URL}/api/pawn/label-templates/preview`, {
          prn_source: prnSource,
          token_map: rules,
          variant_no: form.variant_no,
          copies: form.copies,
          // The loan has to be sample data, but the store does not — send it so
          // the preview shows this branch's real name rather than the
          // catalogue's placeholder.
          store_id: storeId,
        })
        .then((r) => setPreview(r.data?.data || null))
        .catch(() => setPreview(null));
    }, 400);
    return () => clearTimeout(previewTimer.current);
  }, [prnSource, rules, form.variant_no, form.copies, storeId]);

  // ── File upload ──────────────────────────────────────────────────────────
  // Loading replaces the source, the analysis and every mapping, so a file that
  // turns out to be empty, binary or unreadable has to leave the screen alone.
  // The browser happily hands back 0 bytes for a file another program is still
  // writing or a cloud placeholder that was never materialised, and applying
  // that wiped the loaded template while the snackbar called it a success.
  const readFile = (file) => {
    if (!file) return;
    if (/\.[^.]+$/.test(file.name) && !LOADABLE.test(file.name))
      return notify(`${file.name} is not a .prn or .txt file`, "warning");
    if (file.size === 0)
      return notify(
        `${file.name} is empty (0 bytes) — the template on screen is unchanged`,
        "warning",
      );
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      if (!text.trim())
        return notify(
          `${file.name} has no template text — the template on screen is unchanged`,
          "warning",
        );
      setPrnSource(text);
      runScan(text);
      setForm((f) => ({
        ...f,
        template_name: f.template_name || file.name.replace(LOADABLE, ""),
      }));
      notify(`Loaded ${file.name} (${text.length} bytes)`);
    };
    reader.onerror = () => notify("Could not read that file", "error");
    reader.readAsText(file, "ISO-8859-1");
  };

  const onDrop = (e) => {
    e.preventDefault();
    readFile(e.dataTransfer?.files?.[0]);
  };

  // ── Rule editing ─────────────────────────────────────────────────────────
  const patchRule = (token, patch) =>
    setRules((prev) =>
      prev.map((r) => (r.token === token ? { ...r, ...patch } : r)),
    );

  const applyHints = () => {
    let hit = 0;
    setRules((prev) =>
      prev.map((r) => {
        if (r.source || !TOKEN_HINTS[r.token]) return r;
        hit += 1;
        return { ...r, source: TOKEN_HINTS[r.token] };
      }),
    );
    notify(
      hit
        ? `Auto-filled ${hit} mapping${hit > 1 ? "s" : ""}`
        : "Nothing left to auto-fill",
      hit ? "success" : "info",
    );
  };

  // ── Load / reset ─────────────────────────────────────────────────────────
  // `silent` is for the template the screen opens itself: a snackbar announcing
  // something the user never asked for is noise — the highlighted chip already
  // says which one is loaded.
  const loadTemplate = useCallback(async (id, { silent = false } = {}) => {
    try {
      const r = await axios.get(
        `${API_BASE_URL}/api/pawn/label-templates/${id}`,
      );
      const t = r.data?.data;
      if (!t) return;
      setEditingId(t.id);
      setForm({
        template_name: t.template_name || "",
        description: t.description || "",
        printer_model: t.printer_model || "",
        share_name: t.share_name || "",
        variant_no: t.variant_no || 1,
        copies: t.copies || 1,
        is_default: !!t.is_default,
      });
      setPrnSource(t.prn_source || "");
      setAnalysis(t.analysis || null);
      // Stored rules first, then a blank row for any token they don't cover.
      const stored = (t.token_map || []).map((m) => ({
        ...blankRule(m.token),
        ...m,
        maxLen: m.maxLen ?? "",
        padTo: m.padTo ?? "",
        padChar: m.padChar ?? "",
      }));
      const byToken = new Map(stored.map((x) => [x.token, x]));
      setRules(
        (t.analysis?.tokens || stored.map((s) => s.token)).map(
          (tok) => byToken.get(tok) || blankRule(tok),
        ),
      );
      setErrors({});
      if (!silent) notify(`Opened "${t.template_name}"`);
    } catch (e) {
      notify(apiErrorMessage(e) || "Could not open that template", "error");
    }
  }, []);

  // List this store's templates and, on first arrival, open the one that matters:
  // the store default, else the most recently saved. Only an empty store is left
  // showing a blank form.
  useEffect(() => {
    let alive = true;
    loadTemplates().then((rows) => {
      if (!alive || !storeId || autoOpenedFor.current === storeId) return;
      autoOpenedFor.current = storeId;
      const opening = pickOpeningTemplate(rows);
      if (opening) loadTemplate(opening.id, { silent: true });
    });
    return () => {
      alive = false;
    };
  }, [loadTemplates, loadTemplate, storeId]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      template_name: "",
      description: "",
      printer_model: "",
      share_name: "",
      variant_no: 1,
      copies: 1,
      is_default: false,
    });
    setPrnSource("");
    setAnalysis(null);
    setRules([]);
    setPreview(null);
    setErrors({});
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const next = {};
    if (!form.template_name.trim()) next.template_name = true;
    if (!prnSource.trim()) next.prn_source = true;
    setErrors(next);

    if (next.template_name)
      return notify("Give the template a name", "warning");
    if (next.prn_source)
      return notify("Upload or paste the PRN template first", "warning");
    if (!storeId) return notify("No store selected", "error");

    const barcodeUnmapped = (analysis?.barcodeTokens || []).filter(
      (t) => !rules.some((r) => r.token === t && (r.source || r.literal)),
    );
    if (barcodeUnmapped.length)
      return notify(
        `The barcode token <${barcodeUnmapped[0]}> is not mapped — the label would print an empty barcode`,
        "warning",
      );

    setSaving(true);
    try {
      const payload = {
        ...form,
        prn_source: prnSource,
        token_map: rules,
        [editingId ? "updated_by" : "created_by"]: userId,
      };
      if (editingId) {
        await axios.put(
          `${API_BASE_URL}/api/pawn/label-templates/${editingId}`,
          payload,
        );
        notify("Template updated");
      } else {
        const r = await axios.post(
          `${API_BASE_URL}/api/pawn/label-templates/store/${storeId}`,
          payload,
        );
        setEditingId(r.data?.data?.id || null);
        notify("Template saved");
      }
      loadTemplates();
    } catch (e) {
      notify(apiErrorMessage(e) || "Could not save the template", "error");
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (id, name) =>
    setConfirmDelete({ open: true, id, name, busy: false });

  const handleDelete = async () => {
    const { id } = confirmDelete;
    if (!id) return;
    setConfirmDelete((c) => ({ ...c, busy: true }));
    try {
      await axios.delete(`${API_BASE_URL}/api/pawn/label-templates/${id}`);
      if (editingId === id) resetForm();
      loadTemplates();
      setConfirmDelete({ open: false, id: null, name: "", busy: false });
      notify("Template deleted");
    } catch (e) {
      setConfirmDelete((c) => ({ ...c, busy: false }));
      notify(apiErrorMessage(e) || "Could not delete", "error");
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/pawn/label-templates/${id}/default`,
      );
      loadTemplates();
      if (editingId === id) setForm((f) => ({ ...f, is_default: true }));
      notify("Set as the default label for this store");
    } catch (e) {
      notify(apiErrorMessage(e) || "Could not set the default", "error");
    }
  };

  // ── Download the merged bytes ────────────────────────────────────────────
  const downloadMerged = (text, filename) => {
    const blob = new Blob([text], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTestPrint = () => {
    if (!editingId)
      return notify("Save the template first, then test it", "warning");
    setVoucherAsk({ open: true, value: "", busy: false });
  };

  const runTestPrint = async () => {
    const voucher = voucherAsk.value.trim();

    if (!voucher) {
      if (!preview?.text) return notify("Nothing to render yet", "warning");
      downloadMerged(
        preview.text,
        `label_sample_${form.template_name || "test"}.prn`,
      );
      setVoucherAsk({ open: false, value: "", busy: false });
      return notify("Sample label downloaded — send it with copy /b to test");
    }

    setVoucherAsk((v) => ({ ...v, busy: true }));
    try {
      const r = await axios.get(
        `${API_BASE_URL}/api/pawn/label-templates/${editingId}/render/${encodeURIComponent(
          voucher,
        )}`,
      );
      const d = r.data?.data;
      if (!d?.text) {
        setVoucherAsk((v) => ({ ...v, busy: false }));
        return notify("Nothing came back for that voucher", "error");
      }
      downloadMerged(d.text, `label_${voucher.replace(/[^\w-]/g, "_")}.prn`);
      setVoucherAsk({ open: false, value: "", busy: false });
      notify(`Rendered voucher ${d.voucher_no} — file downloaded`);
    } catch (e) {
      setVoucherAsk((v) => ({ ...v, busy: false }));
      notify(apiErrorMessage(e) || "Could not render that voucher", "error");
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const barcodeTokens = useMemo(
    () => new Set(analysis?.barcodeTokens || []),
    [analysis],
  );
  const mappedCount = rules.filter((r) => r.source || r.literal).length;
  const variantMeta =
    analysis?.variants?.[(form.variant_no || 1) - 1]?.meta || {};
  const bakedText =
    analysis?.variants?.[(form.variant_no || 1) - 1]?.bakedText || [];

  // Layout is parsed from the raw template, not from the merged preview text
  const parsedLabel = useMemo(
    () => parseLabel(prnSource, form.variant_no),
    [prnSource, form.variant_no],
  );
  const mappedTokens = useMemo(
    () =>
      new Set(rules.filter((r) => r.source || r.literal).map((r) => r.token)),
    [rules],
  );

  const declared = parsedLabel.declared;
  const declaredDiffers =
    declared.wMm &&
    (Math.abs(declared.wMm - labelMm.w) > 0.5 ||
      (declared.hMm && Math.abs(declared.hMm - labelMm.h) > 0.5));

  // ── Field dropdown options, grouped by catalogue section ─────────────────
  const fieldOptions = useMemo(() => {
    const out = [
      <MenuItem key="__blank" value="">
        <em style={{ color: INK_MUTED }}>Not mapped — prints blank</em>
      </MenuItem>,
      <MenuItem key="__literal" value="literal">
        <em style={{ color: AMBER }}>Fixed text…</em>
      </MenuItem>,
    ];
    for (const g of catalog.groups || []) {
      out.push(
        <ListSubheader
          key={`h_${g.group}`}
          sx={{
            fontSize: 11,
            fontWeight: 800,
            color: BLUE,
            lineHeight: "26px",
            bgcolor: BLUE_PALE,
          }}
        >
          {g.group}
        </ListSubheader>,
      );
      for (const f of g.fields)
        out.push(
          <MenuItem key={f.key} value={f.key} sx={{ fontSize: 13 }}>
            {f.label}
          </MenuItem>,
        );
    }
    return out;
  }, [catalog]);

  return (
    <Box
      sx={{
        // Flex column so the three working columns can take exactly the height
        // left over below the cards: each of them scrolls internally, so nothing
        // the user types — a longer PRN, another keyword — can grow the page.
        // Vertical scroll stays available so a very short window degrades to a
        // page scroll instead of clipping the preview away.
        height: embedded ? "auto" : "100%",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: embedded ? "transparent" : BG_ALT,
        p: embedded ? 0 : { xs: 1.25, sm: 1.5, md: 2.5 },
      }}
    >
      {/* Header & Action Buttons */}
      {!embedded && (
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent={{ xs: "flex-start", md: "space-between" }}
          mb={1.5}
          sx={{ flexShrink: 0 }}
        >
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 1, sm: 1.5 }}
            mb={{ xs: 1.5, md: 2 }}
          >
            <PawnNavButtons />
            <Box
              sx={{
                width: { xs: 36, sm: 42 },
                height: { xs: 36, sm: 42 },
                flexShrink: 0,
                borderRadius: 2,
                bgcolor: BLUE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
              }}
            >
              <QrCodeScannerRoundedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.05rem", sm: "1.25rem" },
                  color: INK,
                }}
              >
                Barcode Mapper
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              justifyContent: { xs: "space-between", md: "flex-end" },
              bgcolor: embedded ? "transparent" : BG_ALT,
            }}
          >
            {editingId && !form.is_default && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<StarRoundedIcon />}
                onClick={() => handleSetDefault(editingId)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12.5,
                  color: BLUE,
                  borderColor: BORDER_MED,
                }}
              >
                Set Default
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              disabled={!preview?.text}
              onClick={handleTestPrint}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: BLUE,
                borderColor: BORDER_MED,
              }}
            >
              Test PRN File
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={14} sx={{ color: "#fff" }} />
                ) : (
                  <SaveRoundedIcon />
                )
              }
              disabled={saving}
              onClick={handleSave}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                fontSize: 12.5,
                px: 2.5,
                bgcolor: BLUE,
                "&:hover": { bgcolor: BLUE_DARK },
              }}
            >
              {editingId ? "Update" : "Save"}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Saved templates & Printer Identification ── */}
      <Grid container spacing={1.5} sx={{ flexShrink: 0 }}>
        {/* ── Saved templates ── */}
        <Grid item xs={12} md={7.75}>
          <Paper sx={{ ...cardSx, mb: 1.5 }}>
            <SectionHeader
              icon={<StarRoundedIcon sx={{ fontSize: 16 }} />}
              label="Saved Templates"
              right={
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={resetForm}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12,
                    color: BLUE,
                  }}
                >
                  New
                </Button>
              }
            />
            {templates.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: INK_MUTED }}>
                None yet. Upload a PRN file below, map its keywords, and save.
              </Typography>
            ) : (
              <Box
                display="flex"
                flexWrap="wrap"
                gap={0.75}
                sx={{
                  // Two rows of chips, then this strip scrolls — a store with a
                  // dozen templates must not push the working columns down.
                  maxHeight: { xs: 90, md: 60 },
                  overflowY: "auto",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {templates.map((t) => (
                  <Chip
                    key={t.id}
                    size="small"
                    onClick={() => loadTemplate(t.id)}
                    icon={
                      t.is_default ? (
                        <StarRoundedIcon sx={{ fontSize: 14 }} />
                      ) : undefined
                    }
                    label={
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {t.template_name}
                        <Box
                          component="span"
                          sx={{ fontSize: 10, color: INK_MUTED }}
                        >
                          {t.language}
                          {t.unmappedTokens?.length
                            ? ` · ${t.unmappedTokens.length} unmapped`
                            : ""}
                        </Box>
                      </Box>
                    }
                    onDelete={() => askDelete(t.id, t.template_name)}
                    deleteIcon={<DeleteOutlineIcon sx={{ fontSize: 15 }} />}
                    variant={editingId === t.id ? "filled" : "outlined"}
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      borderColor: t.is_default ? BLUE : BORDER_MED,
                      ...(editingId === t.id
                        ? {
                            bgcolor: BLUE,
                            color: "#fff",
                            "& .MuiChip-icon, & .MuiChip-deleteIcon": {
                              color: "#fff",
                            },
                          }
                        : { color: INK }),
                    }}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Printer Identification ── */}
        <Grid item xs={12} md={4.25}>
          <Paper sx={{ ...cardSx, mb: 1.5 }}>
            <SectionHeader
              icon={<PrintRoundedIcon sx={{ fontSize: 16 }} />}
              label="Printer & Template"
            />
            {/* Thirds, not twelfths: these `md` widths were written for a
                full-width row, and inside a 4.25-column card they squeezed the
                labels to "Win…" and stacked the helper text one word per line —
                which pushed the working panels below the fold. */}
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={4}>
                <CField
                  label="Template Name *"
                  value={form.template_name}
                  error={errors.template_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, template_name: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CField
                  label="Printer Model"
                  placeholder="SATO SA408"
                  value={form.printer_model}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, printer_model: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CField
                  label="Share Name"
                  placeholder="SATO1"
                  value={form.share_name}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      share_name: e.target.value.toUpperCase(),
                    }))
                  }
                  helperText="Properties → Sharing"
                  FormHelperTextProps={{
                    sx: { fontSize: 10, ml: 0.5, mt: 0.25 },
                  }}
                />
              </Grid>
              {/* <Grid item xs={6} sm={3} md={2}>
                <CField
                  select
                  label="Variant"
                  value={form.variant_no}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      variant_no: Number(e.target.value),
                    }))
                  }
                  disabled={!analysis || analysis.variantCount <= 1}
                >
                  {Array.from(
                    { length: analysis?.variantCount || 1 },
                    (_, i) => (
                      <MenuItem key={i + 1} value={i + 1} sx={{ fontSize: 13 }}>
                        {i + 1}
                      </MenuItem>
                    ),
                  )}
                </CField>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <CField
                  label="Copies"
                  type="number"
                  value={form.copies}
                  inputProps={{ min: 1, max: 9 }}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      copies: Number(e.target.value) || 1,
                    }))
                  }
                />
              </Grid> */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={form.is_default}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, is_default: e.target.checked }))
                      }
                      sx={{
                        color: BORDER_MED,
                        "&.Mui-checked": { color: BLUE },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12.5, color: INK }}>
                      Use this template for labels printed after a payment
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Template Source, Mapping Table & Previewer */}
      <Grid
        container
        spacing={1}
        sx={{
          // Desktop: take the leftover height instead of a fixed 700 that had to
          // be added to the cards above and so overflowed the window. Below md
          // the three panels stack at their natural height and the page scrolls.
          flex: { xs: "0 0 auto", md: 1 },
          minHeight: { xs: 0, md: 380 },
          minWidth: 0,
        }}
      >
        {/* ── LEFT: template source ── */}
        <Grid item xs={12} md={2.5}>
          <Paper
            sx={{
              ...cardSx,
              height: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SectionHeader
              icon={<UploadFileRoundedIcon sx={{ fontSize: 16 }} />}
              label="PRN Template"
              right={
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<UploadFileRoundedIcon />}
                  onClick={() => fileRef.current?.click()}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: BLUE,
                    "&:hover": { bgcolor: BLUE_DARK },
                  }}
                >
                  Upload
                </Button>
              }
            />
            <input
              ref={fileRef}
              type="file"
              accept=".prn,.txt,text/plain"
              hidden
              onChange={(e) => {
                readFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />

            <Box
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <TextField
                multiline
                fullWidth
                // Desktop: a fixed box that fills the card and scrolls its own
                // text. Autosizing rows (`minRows`/`maxRows`) grow with the
                // template, which is what used to stretch this column past the
                // window. Phones keep autosizing — the page scrolls there anyway.
                {...(isMobile ? { minRows: 8, maxRows: 12 } : { rows: 2 })}
                placeholder={
                  "Drop a .prn / .txt file here, click Upload, or paste the template text…\n\n" +
                  "^XA\n^FO18,29\n^BY2^BCN,36,N,N,,A^FD<TAGNO>^FS\n^XZ"
                }
                value={prnSource}
                onChange={(e) => setPrnSource(e.target.value)}
                onBlur={() => runScan(prnSource)}
                error={!!errors.prn_source}
                sx={{
                  ...(isMobile
                    ? null
                    : { flex: 1, minHeight: 0, display: "flex" }),
                  "& .MuiInputBase-root": {
                    fontFamily: "Consolas, 'Courier New', monospace",
                    fontSize: 11.5,
                    lineHeight: 1.5,
                    borderRadius: 2,
                    alignItems: isMobile ? "flex-start" : "stretch",
                    background: errors.prn_source ? "#FEF2F2" : "#FBFCFE",
                    ...(isMobile
                      ? null
                      : { flex: 1, height: "100%", overflow: "hidden" }),
                  },
                  ...(isMobile
                    ? null
                    : {
                        // The textarea, not the card, is the scroller. Scrollbar
                        // hidden to match the mapping table beside it.
                        "& textarea": {
                          height: "100% !important",
                          overflowY: "auto !important",
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                          "&::-webkit-scrollbar": { display: "none" },
                        },
                      }),
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: errors.prn_source ? DANGER : BORDER_MED,
                      borderWidth: errors.prn_source ? "2px" : "1px",
                      borderStyle: "dashed",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: BLUE,
                      borderStyle: "solid",
                    },
                  },
                }}
              />
              <Box display="flex" alignItems="center" gap={1} mt={0.75}>
                <Button
                  size="small"
                  onClick={() => runScan(prnSource)}
                  disabled={scanning || !prnSource.trim()}
                  startIcon={
                    scanning ? (
                      <CircularProgress size={13} />
                    ) : (
                      <QrCodeScannerRoundedIcon />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12,
                    color: BLUE,
                  }}
                >
                  {scanning ? "Scanning…" : "Find keywords"}
                </Button>
                <Typography sx={{ fontSize: 11, color: INK_MUTED, ml: "auto" }}>
                  {prnSource ? `${prnSource.length} bytes` : ""}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* ── MIDDLE: mapping table ── */}
        <Grid
          item
          xs={12}
          md={5.25}
          sx={{
            minHeight: 0,
            height: isMobile ? "auto" : "100%",
          }}
        >
          <Paper
            sx={{
              ...cardSx,
              height: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <SectionHeader
              icon={<QrCodeScannerRoundedIcon sx={{ fontSize: 16 }} />}
              label={`Keyword Mapping${
                rules.length ? ` — ${mappedCount}/${rules.length} mapped` : ""
              }`}
              right={
                rules.length > 0 && (
                  <Button
                    size="small"
                    startIcon={<AutoFixHighRoundedIcon />}
                    onClick={applyHints}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 12,
                      color: BLUE,
                    }}
                  >
                    Auto-fill
                  </Button>
                )
              }
            />

            {rules.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 160,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: INK_MUTED,
                  gap: 0.5,
                }}
              >
                <QrCodeScannerRoundedIcon
                  sx={{
                    fontSize: 34,
                    opacity: 0.35,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 700,
                  }}
                >
                  No keywords yet
                </Typography>

                <Typography
                  sx={{
                    fontSize: 11.5,
                    maxWidth: 320,
                  }}
                >
                  Upload or paste the PRN template on the left. Every{" "}
                  <code>&lt;KEYWORD&gt;</code> found in it appears here for
                  mapping.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  // Take only the remaining space below SectionHeader
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,

                  // ONLY this area scrolls
                  overflowY: "auto",
                  overflowX: "auto",

                  // Hide scrollbar while keeping scrolling enabled
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 520,
                    // MUI's 16px side padding on five columns is 160px of air —
                    // enough to push PREVIEW, the column the whole table exists
                    // for, out behind the (hidden) horizontal scrollbar.
                    "& td, & th": { px: 1 },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          bgcolor: BG_ALT,
                          borderBottom: `1px solid ${BORDER}`,
                          py: 0.75,
                        },
                      }}
                    >
                      <TableCell sx={labelSx}>KEYWORD</TableCell>
                      <TableCell sx={labelSx}>FILL FROM</TableCell>
                      <TableCell sx={labelSx}>FORMAT</TableCell>
                      <TableCell
                        sx={{
                          ...labelSx,
                          width: 56,
                        }}
                      >
                        MAX
                      </TableCell>
                      <TableCell sx={labelSx}>PREVIEW</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rules.map((r) => {
                      const isBarcode = barcodeTokens.has(r.token);
                      const unset = !r.source && !r.literal;

                      return (
                        <TableRow
                          key={r.token}
                          sx={{
                            "& td": {
                              borderBottom: `1px solid ${BORDER}`,
                              py: 0.6,
                            },
                            bgcolor:
                              isBarcode && unset ? "#FEF2F2" : "transparent",
                          }}
                        >
                          {/* KEYWORD */}
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography
                                sx={{
                                  fontFamily: "Consolas, monospace",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: INK,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                &lt;{r.token}&gt;
                              </Typography>

                              {isBarcode && (
                                <Tooltip title="This keyword is what the barcode encodes — it must be mapped or the label prints an unscannable barcode">
                                  <Chip
                                    size="small"
                                    label="BARCODE"
                                    sx={{
                                      height: 16,
                                      fontSize: 8.5,
                                      fontWeight: 800,
                                      bgcolor: unset ? DANGER : SUCCESS,
                                      color: "#fff",
                                      "& .MuiChip-label": {
                                        px: 0.6,
                                      },
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>

                          {/* FILL FROM */}
                          <TableCell>
                            <CField
                              select
                              value={r.source}
                              error={isBarcode && unset}
                              onChange={(e) =>
                                patchRule(r.token, {
                                  source: e.target.value,
                                })
                              }
                              sx={{
                                minWidth: 150,
                              }}
                            >
                              {fieldOptions}
                            </CField>

                            {r.source === "literal" && (
                              <CField
                                placeholder="Text to print"
                                value={r.literal}
                                onChange={(e) =>
                                  patchRule(r.token, {
                                    literal: e.target.value,
                                  })
                                }
                                sx={{
                                  mt: 0.5,
                                  minWidth: 150,
                                }}
                              />
                            )}
                          </TableCell>

                          {/* FORMAT */}
                          <TableCell>
                            <CField
                              select
                              value={r.transform || "none"}
                              onChange={(e) =>
                                patchRule(r.token, {
                                  transform: e.target.value,
                                })
                              }
                              sx={{
                                minWidth: 104,
                              }}
                            >
                              {(catalog.transforms || []).map((t) => (
                                <MenuItem
                                  key={t.key}
                                  value={t.key}
                                  sx={{
                                    fontSize: 13,
                                  }}
                                >
                                  {t.label}
                                </MenuItem>
                              ))}
                            </CField>
                          </TableCell>

                          {/* MAX */}
                          <TableCell>
                            <Tooltip title="Cut the value off after this many characters so long values cannot run off the edge of the label">
                              <CField
                                type="number"
                                placeholder="—"
                                value={r.maxLen}
                                inputProps={{
                                  min: 0,
                                  max: 99,
                                }}
                                onChange={(e) =>
                                  patchRule(r.token, {
                                    maxLen: e.target.value,
                                  })
                                }
                                sx={{
                                  width: 56,
                                }}
                              />
                            </Tooltip>
                          </TableCell>

                          {/* PREVIEW */}
                          <TableCell>
                            <Typography
                              sx={{
                                fontFamily: "Consolas, monospace",
                                fontSize: 11.5,
                                color: unset ? INK_MUTED : INK,
                                fontStyle: unset ? "italic" : "normal",
                                whiteSpace: "pre",
                                maxWidth: 110,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {unset
                                ? "(blank)"
                                : (preview?.resolved?.[r.token] ?? "…") ||
                                  "(empty)"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── RIGHT: live label previewer ── */}
        <Grid item xs={12} md={4.25}>
          <Paper
            sx={{
              ...cardSx,
              height: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <SectionHeader
              icon={<VisibilityRoundedIcon sx={{ fontSize: 16 }} />}
              label="Label Preview"
              right={
                <Box display="flex" gap={0.4}>
                  {[1, 2, 3].map((z) => (
                    <Chip
                      key={z}
                      size="small"
                      label={z === 1 ? "Fit" : `${z}×`}
                      onClick={() => setZoom(z)}
                      variant={zoom === z ? "filled" : "outlined"}
                      sx={{
                        height: 20,
                        fontSize: 10.5,
                        fontWeight: 800,
                        "& .MuiChip-label": { px: 0.7 },
                        ...(zoom === z
                          ? { bgcolor: BLUE, color: "#fff" }
                          : { color: INK_MUTED, borderColor: BORDER_MED }),
                      }}
                    />
                  ))}
                </Box>
              }
            />

            {/* Everything below the heading scrolls inside the card. The size
                controls are fixed-height, but the preview's warning list and the
                merged-PRN dump are not, and on desktop this card is a fixed slice
                of the window. */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                overflowX: "hidden",
                // Room for the first row's floating labels, which sit above the
                // field and would otherwise be shaved off by the scroll edge.
                pt: 0.75,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {/* Tag stock size — what the label is physically printed on */}
              <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                mb={1}
                flexShrink={0}
              >
                <CField
                  label="Width mm"
                  type="number"
                  value={labelMm.w}
                  inputProps={{ min: 5, max: 300, step: 1 }}
                  onChange={(e) =>
                    setLabelMm((s) => ({
                      ...s,
                      w: Math.max(
                        5,
                        Math.min(300, Number(e.target.value) || 0),
                      ),
                    }))
                  }
                />
                <Typography sx={{ fontSize: 12, color: INK_MUTED }}>
                  ×
                </Typography>
                <CField
                  label="Height mm"
                  type="number"
                  value={labelMm.h}
                  inputProps={{ min: 5, max: 300, step: 1 }}
                  onChange={(e) =>
                    setLabelMm((s) => ({
                      ...s,
                      h: Math.max(
                        5,
                        Math.min(300, Number(e.target.value) || 0),
                      ),
                    }))
                  }
                />
                {declaredDiffers && (
                  <Tooltip
                    title={`The template declares ${declared.wMm}${
                      declared.hMm ? ` × ${declared.hMm}` : ""
                    } mm`}
                  >
                    <Chip
                      size="small"
                      label={`Use ${declared.wMm}${
                        declared.hMm ? `×${declared.hMm}` : ""
                      }`}
                      onClick={() =>
                        setLabelMm((s) => ({
                          w: declared.wMm,
                          h: declared.hMm || s.h,
                        }))
                      }
                      sx={{
                        height: 22,
                        fontSize: 10.5,
                        fontWeight: 800,
                        color: BLUE,
                        borderColor: BLUE,
                        flexShrink: 0,
                      }}
                      variant="outlined"
                    />
                  </Tooltip>
                )}
              </Box>

              {/* Tag stock die cut. Defaults describe the shop's jewellery tag —
                body plus wrap-around strap; a shop using plain rectangular stock
                switches it off. */}
              <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                mb={1}
                flexShrink={0}
              >
                <CField
                  select
                  label="Tag shape"
                  value={tag.shape}
                  onChange={(e) =>
                    setTag((t) => ({ ...t, shape: e.target.value }))
                  }
                  sx={{ minWidth: 118 }}
                >
                  <MenuItem value="tag" sx={{ fontSize: 13 }}>
                    Jewellery tag
                  </MenuItem>
                  <MenuItem value="rect" sx={{ fontSize: 13 }}>
                    Plain rectangle
                  </MenuItem>
                </CField>
                {tag.shape === "tag" && (
                  <>
                    <Tooltip title="Length of the body — the part that stays readable. Everything past it is strap, wrapped around the ornament.">
                      <CField
                        label="Body mm"
                        type="number"
                        value={tag.printArea}
                        inputProps={{ min: 5, max: labelMm.w - 3, step: 1 }}
                        onChange={(e) =>
                          setTag((t) => ({
                            ...t,
                            printArea: Math.max(
                              5,
                              Math.min(
                                labelMm.w - 3,
                                Number(e.target.value) || 0,
                              ),
                            ),
                          }))
                        }
                      />
                    </Tooltip>
                    <Tooltip title="Thickness of the narrow strap that wraps around the ring shank or chain">
                      <CField
                        label="Strap mm"
                        type="number"
                        value={tag.tailHeight}
                        inputProps={{ min: 0.5, max: labelMm.h, step: 0.5 }}
                        onChange={(e) =>
                          setTag((t) => ({
                            ...t,
                            tailHeight: Math.max(
                              0.5,
                              Math.min(labelMm.h, Number(e.target.value) || 0),
                            ),
                          }))
                        }
                      />
                    </Tooltip>
                  </>
                )}
              </Box>

              <FormControlLabel
                sx={{ ml: 0, mb: 0.5, flexShrink: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={showUnmapped}
                    onChange={(e) => setShowUnmapped(e.target.checked)}
                    sx={{
                      p: 0.5,
                      color: BORDER_MED,
                      "&.Mui-checked": { color: AMBER },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 11.5, color: INK_MUTED }}>
                    Show unmapped keywords{" "}
                    <Box component="span" sx={{ color: BORDER_MED }}>
                      · they print nothing
                    </Box>
                  </Typography>
                }
              />

              {/* Wrapped rather than left as a direct flex child: with the panel
                now scrolling, the preview should sit at its natural height
                instead of being the one thing that gets squeezed when the
                warnings list is long. */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexShrink: 0,
                  minWidth: 0,
                }}
              >
                <LabelPreview
                  parsed={parsedLabel}
                  resolved={preview?.resolved}
                  mappedTokens={mappedTokens}
                  widthMm={labelMm.w}
                  heightMm={labelMm.h}
                  zoom={zoom}
                  shape={tag.shape}
                  printAreaMm={tag.printArea}
                  tailHeightMm={tag.tailHeight}
                  showUnmapped={showUnmapped}
                />
              </Box>

              {/* What the design itself dictates and no mapping can change — worth
                a glance before saving, e.g. a store name baked into the artwork
                that ought to be a token in a multi-branch setup. */}
              {(Object.keys(variantMeta).length > 0 ||
                bakedText.length > 0) && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    mt: 0.75,
                    flexShrink: 0,
                  }}
                >
                  {[
                    variantMeta.barcodeHeightMm &&
                      `Barcode ${variantMeta.barcodeHeightMm} mm tall`,
                    variantMeta.barcodeHumanReadable === false &&
                      "No number under the barcode",
                    variantMeta.darkness && `Darkness ${variantMeta.darkness}`,
                    variantMeta.ribbon ? "Ribbon (thermal transfer)" : null,
                  ]
                    .filter(Boolean)
                    .map((label) => (
                      <Chip
                        key={label}
                        size="small"
                        label={label}
                        sx={{
                          height: 19,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: BLUE_PALE,
                          color: BLUE_DARK,
                          "& .MuiChip-label": { px: 0.7 },
                        }}
                      />
                    ))}
                  {bakedText.length > 0 && (
                    <Tooltip
                      title={`Printed on every label regardless of the loan: ${bakedText.join(
                        " · ",
                      )}`}
                    >
                      <Chip
                        size="small"
                        label={`${bakedText.length} fixed text`}
                        sx={{
                          height: 19,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: AMBER_BG,
                          color: AMBER,
                          "& .MuiChip-label": { px: 0.7 },
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              )}

              {/* The bytes behind the picture — the last resort when a design does
                something the previewer draws differently to the printer. */}
              {preview?.text && (
                <Box sx={{ mt: 0.5, flexShrink: 0 }}>
                  <Button
                    size="small"
                    onClick={() => setShowRaw((v) => !v)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 11.5,
                      color: INK_MUTED,
                      px: 0.5,
                      minWidth: 0,
                    }}
                  >
                    {showRaw ? "Hide" : "Show"} merged PRN ·{" "}
                    {preview.byteLength} bytes
                  </Button>
                  {showRaw && (
                    <Box
                      sx={{
                        maxHeight: 140,
                        overflow: "auto",
                        border: `1px solid ${BORDER}`,
                        borderRadius: 1.5,
                        bgcolor: "#FBFCFE",
                        p: 0.85,
                        fontFamily: "Consolas, 'Courier New', monospace",
                        fontSize: 10.5,
                        lineHeight: 1.45,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        color: INK,
                      }}
                    >
                      {preview.text}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Delete confirmation ── */}
      <Dialog
        open={confirmDelete.open}
        onClose={() =>
          !confirmDelete.busy &&
          setConfirmDelete((c) => ({ ...c, open: false }))
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: 15,
            fontWeight: 800,
            color: INK,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: 1.5,
              bgcolor: `${DANGER}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: DANGER,
              flexShrink: 0,
            }}
          >
            <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
          Delete label template?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <DialogContentText sx={{ fontSize: 13, color: INK_MUTED }}>
            <Box component="span" sx={{ fontWeight: 700, color: INK }}>
              “{confirmDelete.name}”
            </Box>{" "}
            and its keyword mapping will be removed. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0.5 }}>
          <Button
            size="small"
            onClick={() => setConfirmDelete((c) => ({ ...c, open: false }))}
            disabled={confirmDelete.busy}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12.5,
              color: INK_MUTED,
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleDelete}
            disabled={confirmDelete.busy}
            startIcon={
              confirmDelete.busy ? (
                <CircularProgress size={13} sx={{ color: "#fff" }} />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: 12.5,
              bgcolor: DANGER,
              "&:hover": { bgcolor: "#B91C1C" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Test-print voucher prompt ── */}
      <Dialog
        open={voucherAsk.open}
        onClose={() =>
          !voucherAsk.busy && setVoucherAsk((v) => ({ ...v, open: false }))
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: 15,
            fontWeight: 800,
            color: INK,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: 1.5,
              bgcolor: `${BLUE}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: BLUE,
              flexShrink: 0,
            }}
          >
            <DownloadRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
          Test print file
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <DialogContentText sx={{ fontSize: 12.5, color: INK_MUTED, mb: 1.5 }}>
            Enter the voucher number of a real loan to render it with live data,
            or leave it blank to download the sample label.
          </DialogContentText>
          <CField
            autoFocus
            label="Voucher No"
            placeholder="Leave blank for sample data"
            value={voucherAsk.value}
            onChange={(e) =>
              setVoucherAsk((v) => ({ ...v, value: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && !voucherAsk.busy) runTestPrint();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0.5 }}>
          <Button
            size="small"
            onClick={() => setVoucherAsk((v) => ({ ...v, open: false }))}
            disabled={voucherAsk.busy}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: 12.5,
              color: INK_MUTED,
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={runTestPrint}
            disabled={voucherAsk.busy}
            startIcon={
              voucherAsk.busy ? (
                <CircularProgress size={13} sx={{ color: "#fff" }} />
              ) : (
                <DownloadRoundedIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: 12.5,
              bgcolor: BLUE,
              "&:hover": { bgcolor: BLUE_DARK },
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ fontSize: 13, borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PawnBarcodeMapper;
